import type { RecursiveSearchIndex } from './useRecursiveSearchMap';

export interface FlatRecursiveRow<T = any> {
  key: string;
  node: T;
  depth: number;
  hasChildren: boolean;
  hasRawChildren: boolean;
  chain: any[];
}

export function flattenRecursiveTree<T>(params: {
  roots: T[];
  index: RecursiveSearchIndex<T>;
  expandedKeys: Set<string>;
  autoExpandedKeys: Set<string>;
  searchActive: boolean;
  visibleKeys: Set<string>;
  getChildren: (node: T) => T[];
  getKey: (node: T, parentKey: string | null, index: number) => string;
  appendToChain: (chain: any[], node: T) => any[];
  shouldIncludeNode?: (node: T) => boolean;
}): FlatRecursiveRow<T>[] {
  const rows: FlatRecursiveRow<T>[] = [];

  const canRenderNode = (node: T, parentKey: string | null, index: number) => {
    if (params.shouldIncludeNode && !params.shouldIncludeNode(node)) return false;
    if (!params.searchActive) return true;
    return params.visibleKeys.has(params.getKey(node, parentKey, index));
  };

  const visit = (nodes: T[], depth: number, parentKey: string | null, chain: any[]) => {
    nodes.forEach((node, index) => {
      if (!canRenderNode(node, parentKey, index)) return;

      const key = params.getKey(node, parentKey, index);
      const children = params.getChildren(node) || [];
      const hasRawChildren = children.length > 0;
      // Шеврон показываем только когда у узла есть реально отображаемые дети.
      const hasChildren = children.some((child, childIndex) => canRenderNode(child, key, childIndex));
      const nextChain = params.appendToChain(chain, node);

      rows.push({ key, node, depth, hasChildren, hasRawChildren, chain: nextChain });

      const isExpanded = params.expandedKeys.has(key)
        || (params.searchActive && params.autoExpandedKeys.has(key));

      if (hasChildren && isExpanded) {
        visit(children, depth + 1, key, nextChain);
      }
    });
  };

  visit(params.roots || [], 0, null, []);
  return rows;
}
